// src/components/store/QuickCartComponents/OrderSummary.jsx
import React, { useState, useEffect } from "react";
import { MapPin, Calendar } from "lucide-react";
import { useSalesContext } from "../../../contexts/SalesContext";
import DividerComponent from "../../ui/DividerComponent";
import { CheckTermsComponent } from "./CheckTermsComponent";
import {
  formatPrice,
  formatDate,
  formatTimeSlot,
} from "../../../scripts/generalScript";

// --- Constantes ---
const BOTTLE_PRICE_RAW = 15000;
const BOTTLE_PRICE_FORMATTED = formatPrice(BOTTLE_PRICE_RAW);
const DISPENSER_PRICE_CURRENT = formatPrice(0);

export const OrderSummary = () => {
  const {
    imageAddress,
    selectedAddress,
    selectedSugs,
    selectedDispensers,
    selectedDeliveryDate,
    selectedTimeSlot,
    deliveryFee,
    selectedZone,
    newOrderTotal,
  } = useSalesContext();

  const [imageUrl, setImageUrl] = useState(null);
  const bottleTotal = (selectedSugs || 0) * BOTTLE_PRICE_RAW;
  const totalDeliveryFee = deliveryFee * (selectedSugs || 0);

  useEffect(() => {
    if (imageAddress) {
      if (imageAddress instanceof Blob) {
        const url = URL.createObjectURL(imageAddress);
        setImageUrl(url);
        return () => URL.revokeObjectURL(url);
      } else if (typeof imageAddress === "string") {
        setImageUrl(imageAddress);
      }
    } else {
      setImageUrl(null);
    }
  }, [imageAddress]);

  const DeliveryInfo = () => (
    <div className="flex flex-col gap-4">
      {selectedDeliveryDate && (
        <div>
          <h3 className="text-lg font-medium mb-4">Fecha de entrega</h3>
          <div className="bg-gray-50 p-4 rounded-xl">
            <div className="flex items-start gap-3">
              <Calendar
                className="text-gray-500 mt-1 flex-shrink-0"
                size={20}
              />
              <div className="flex gap-2">
                <span className="text-base font-medium text-gray-800 block">
                  {formatDate(selectedDeliveryDate)}
                </span>
                <span className="text-base text-gray-600">
                  Por la {formatTimeSlot(selectedTimeSlot)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      <DividerComponent
        size={1}
        color="ebebeb"
        margin={16}
        direction="horizontal"
      />
      {selectedAddress && (
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-medium p-0 m-0">Dirección de entrega</h3>
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 mt-1">
              <MapPin className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-normal text-base text-gray-600 block leading-relaxed">
                {selectedAddress}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {imageUrl ? (
          <div className="w-full">
            <img
              src={imageUrl}
              alt="Dirección seleccionada"
              className="rounded-xl object-cover h-48 w-full"
              onError={(e) => {
                console.error("Error cargando imagen:", e);
                e.target.style.display = "none";
              }}
            />
          </div>
        ) : imageAddress ? (
          <div className="bg-gray-100 h-48 rounded-xl flex items-center justify-center">
            <div className="flex items-center gap-2 text-gray-500">
              <MapPin className="w-6 h-6" />
              <span>Cargando imagen...</span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  const OrderItems = () => (
    <div className="flex flex-col gap-6">
      <div className="bg-gray-50 p-6 rounded-xl h-fit md:sticky md:top-8">
        <h3 className="text-lg font-medium p-0 m-0 mb-6">Resumen del pedido</h3>
        <div className="flex flex-col gap-4 text-base font-regular">
          {selectedSugs > 0 && (
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <div className="flex flex-col">
                <span className="text-gray-800">Botellones de agua</span>
                <span className="text-sm text-gray-600">
                  ${BOTTLE_PRICE_FORMATTED} x {selectedSugs} ud.
                </span>
              </div>
              <div className="text-right">
                <span className="font-normal text-xl text-gray-800">
                  ${formatPrice(bottleTotal)}
                </span>
              </div>
            </div>
          )}

          {selectedDispensers > 0 && (
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <div className="flex flex-col">
                <span className="text-gray-800">Dispensadores</span>
                <span className="text-sm text-gray-600">
                  Gratis x {selectedDispensers} ud.
                </span>
              </div>
              <div className="text-right">
                <span className="font-normal text-xl text-gray-800">
                  ${DISPENSER_PRICE_CURRENT}
                </span>
              </div>
            </div>
          )}

          {/* REEMPLAZAR esta sección: */}
          <div className="pb-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div className="flex flex-col">
                <span className="text-gray-800">Costo de entrega</span>
                <span className="text-sm text-gray-600">
                  {deliveryFee > 0
                    ? `$${formatPrice(deliveryFee)} x ${selectedSugs} ud.`
                    : `$0 x ${selectedSugs} ud.`}
                </span>
              </div>
              <div className="text-right">
                <span className="font-normal text-xl text-gray-800">
                  {deliveryFee > 0
                    ? `$${formatPrice(deliveryFee * selectedSugs)}`
                    : "$0"}
                </span>
              </div>
            </div>
          </div>

          {/* Y en la sección del total, asegúrate de que use newOrderTotal del contexto: */}
          <div className="mt-6  border-gray-300">
            <div className="flex justify-between items-center">
              <span className="text-xl font-medium text-gray-900">Total</span>
              <span className="text-2xl font-medium">
                ${formatPrice(newOrderTotal)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden md:block">
        <CheckTermsComponent />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-8">
      {/* Layout móvil: Una columna */}

      <div className="md:hidden">
        {imageUrl && (
          <div className="my-6">
            <img
              src={imageUrl}
              alt="Dirección seleccionada"
              className="rounded-2xl object-cover h-32 w-full"
              onError={(e) => {
                console.error("Error cargando imagen:", e);
                e.target.style.display = "none";
              }}
            />
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-lg font-medium p-0 m-0 mb-4">
            Dirección de entrega
          </h3>
          {selectedAddress && (
            <div className="space-y-4">
              <span className="font-normal text-base text-gray-600 block mb-4">
                {selectedAddress}
              </span>
            </div>
          )}

          <DividerComponent
            size={1}
            color="ebebeb"
            margin={16}
            direction="horizontal"
          />
        </div>

        <DividerComponent
          size={1}
          color="ebebeb"
          margin={16}
          direction="horizontal"
        />

        <OrderItems />

        <DividerComponent
          size={1}
          color="ebebeb"
          margin={16}
          direction="horizontal"
        />

        <CheckTermsComponent />
      </div>

      {/* Layout desktop: Dos columnas */}
      <div className="hidden md:grid md:grid-cols-2 md:gap-12 md:items-start">
        <DeliveryInfo />
        <OrderItems />
      </div>
    </div>
  );
};

export default OrderSummary;
